import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { LegacyOrders } from "./LegacyOrders";

const STATUS = { pending: "En attente", sent: "Email envoyé", invalid: "Adresse refusée", failed: "Échec", optout: "Désinscrite" };
const CAMPAIGN = (c) => (c?.startsWith("flash-") ? `Vente flash ${c.slice(6)}` : c === "nouveau-site" ? "Nouveau site · RETOUR15" : "");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

function Stats({ s, onToggle }) {
  const by = s?.by_status || {};
  const items = [["Total", s?.total ?? 0], ["Envoyés", by.sent ?? 0], ["En attente", by.pending ?? 0], ["Refusées", (by.invalid ?? 0) + (by.failed ?? 0)], ["Désinscrites", by.optout ?? 0], ["Dernière heure", s?.sent_last_hour ?? 0]];
  return (
    <div className="flex flex-wrap items-end gap-6">
      {items.map(([l, v]) => (
        <div key={l} data-testid={`legacy-stat-${l}`}><p className="text-xs uppercase tracking-[0.16em] text-[var(--parchment)]/70">{l}</p><p className="font-display text-3xl">{Number(v).toLocaleString("fr-FR")}</p></div>
      ))}
      <button type="button" data-testid="legacy-toggle" onClick={onToggle}
        className="ml-auto flex h-11 items-center gap-2 rounded-full border border-[var(--gold)] px-5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]">
        {s?.enabled ? <><Pause className="h-4 w-4" /> Mettre en pause</> : <><Play className="h-4 w-4" /> Reprendre l'envoi</>}
      </button>
    </div>
  );
}

export function LegacyCustomers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const stats = useQuery({ queryKey: ["legacy-stats"], queryFn: () => api.get("/admin/legacy").then((r) => r.data), refetchInterval: 30000 });
  const list = useQuery({ queryKey: ["legacy-list", q, status, page], queryFn: () => api.get("/admin/legacy/list", { params: { q, status, page } }).then((r) => r.data) });
  const items = Array.isArray(list.data?.items) ? list.data.items : [];
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 50));
  const toggle = async () => {
    try {
      await api.post(stats.data?.enabled ? "/admin/legacy/pause" : "/admin/legacy/start");
      qc.invalidateQueries({ queryKey: ["legacy-stats"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section data-testid="admin-legacy" className="space-y-8">
      <div>
        <h3 className="font-display text-3xl">Anciennes clientes · ozentya.com</h3>
        <p className="mt-2 max-w-3xl text-sm text-[var(--parchment)]/80">Liste importée depuis Oxatis. Chaque adresse reçoit un seul email « Ozentya a un nouveau site » avec le code RETOUR15. Envoi progressif (du plus récent au plus ancien) pour respecter la limite du service d'emails.</p>
      </div>
      <Stats s={stats.data} onToggle={toggle} />
      <LegacyOrders />
      <div className="flex flex-wrap gap-3">
        <label className="flex h-11 flex-1 items-center gap-2 rounded-full border border-[var(--line)] px-4">
          <Search className="h-4 w-4 text-[var(--muted)]" />
          <input data-testid="legacy-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher un email, un prénom, un pays" className="w-full bg-transparent text-sm outline-none" />
        </label>
        <select data-testid="legacy-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-11 rounded-full border border-[var(--line)] bg-[var(--ink)] px-4 text-sm">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-[0.16em] text-[var(--parchment)]/70"><th className="py-2">Email</th><th>Prénom</th><th>Pays</th><th>Compte créé</th><th>Statut</th><th>Envoi</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.email} data-testid={`legacy-row-${r.email}`} className="border-t border-[var(--line)]">
                <td className="py-3 pr-3">{r.email}</td><td>{r.first_name}</td><td>{r.country}</td><td>{r.created}</td>
                <td className={r.status === "sent" ? "text-[var(--gold-soft)]" : ""}>{STATUS[r.status] || r.status}</td>
                <td className="text-xs text-[var(--parchment)]/80">{CAMPAIGN(r.campaign)} {fmt(r.sent_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && !list.isLoading ? <p data-testid="legacy-empty" className="mt-4 text-sm text-[var(--parchment)]/80">Aucune adresse ne correspond.</p> : null}
      </div>
      <div className="flex items-center gap-4 text-sm">
        <button type="button" data-testid="legacy-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-[var(--line)] px-4 py-2 disabled:opacity-40">Précédent</button>
        <span data-testid="legacy-page">Page {page} / {pages} · {(list.data?.total ?? 0).toLocaleString("fr-FR")} adresses</span>
        <button type="button" data-testid="legacy-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-full border border-[var(--line)] px-4 py-2 disabled:opacity-40">Suivant</button>
      </div>
    </section>
  );
}
